import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  CommonModule,
  CurrencyPipe,
  DecimalPipe,
  NgIf,
  PercentPipe,
} from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// Material Imports
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

// Bibliotecas de terceiros
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';

// Componentes e serviços
import { CarregamentoService } from '../../services/carregamento/carregamento.service';
import {
  DownloadRota,
  LayoutResultadoService,
} from '../../layouts/layout-resultado/layout-resultado.service';
import { ReceitaEducacaoService } from '../../services/receita-educacao/receita-educacao.service';
import { DadoDespesaService } from '../../services/dado-despesa/dado-despesa.service';
import { DespesasCorrentesService } from '../../services/despesas-correntes/despesas-correntes.service';
import { MemoriaDeCalculoService } from '../../services';

// DTOs
import { ReceitaEducacaoDto } from '../../dtos/receita-educacao-dto';
import { DadoDespesaDto } from '../../dtos/dado-despesa-dto';
import { DespesasCorrentesDto } from '../../dtos/despesas-correntes-dto';
import { MemoriaDeCalculoDto } from '../../dtos/memoria-de-calculo';
import { DespesaCapitalService } from '../../services/despesa-capital/despesa-capital.service';
import { DespesaCapitalDto } from '../../dtos/despesa-capital-dto';
import { LocalizacaoService } from '../../services/localizacao/localizacao.service';
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import {
  LocalizacaoSelecionada,
  PlanoEducacional,
} from '../../interfaces/location.interface';
import { HeaderInfo, PdfUtils } from '../../utils/pdf-utils';
import { CaqEscolasRespostaDto } from '../../dtos/caq-escola-resumo-dto';
import { EscolasCaqService } from '../../services/escolas-caq/escolas-caq.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

// Interface para controlar cada linha da tabela visualmente
export interface LinhaRelatorio {
  id: number;
  descricao: string;
  valor: number;
  percentual: number;
  isTitulo: boolean;
  nivelRecuo: number;
  mostrarBarra: boolean;
}

@Component({
  selector: 'app-receitas-despesas',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    NgIf,
    RouterModule,
    FormsModule,
    NgxEchartsModule,
    DecimalPipe,
    CurrencyPipe,
  ],
  providers: [
    {
      provide: NGX_ECHARTS_CONFIG,
      useValue: { echarts: () => import('echarts') },
    },
  ],
  templateUrl: './relatorio-receitas-despesas.component.html',
  styleUrl: './relatorio-receitas-despesas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelatorioReceitasDespesasComponent implements OnInit {
  // Variáveis dinâmicas do Cabeçalho
  titulo: string = '';
  uf: string = '';
  municipio: string = '';
  codigoIbge: string | number = '';
  dependenciaAdministrativa: string = '';
  localizacaoSelecionada?: LocalizacaoSelecionada | null;
  public planoSelecionado: PlanoEducacional | undefined;

  readonly anoProjecao = 2024;
  readonly anoRealizado = 2023;
  dataAtual = new Date();
  dia = String(this.dataAtual.getDate()).padStart(2, '0'); // Obtém o dia com dois dígitos
  mes = String(this.dataAtual.getMonth() + 1).padStart(2, '0'); // Obtém o mês (mês inicia em 0)

  textoParaCopiar = `SIMULADOR de Custo-Aluno Qualidade: Valores do Custo-Aluno Qualidade (CAQ) para o Brasil, 2024. Disponível em: <https://simcaq.c3sl.ufpr.br/caq-result>. Acesso em: ${this.dia}/${this.mes}/${this.dataAtual.getFullYear()}.`;

  // Dados brutos da API
  receitaEspecifica: ReceitaEducacaoDto | null = null;
  despesasCorrentes: DespesasCorrentesDto | null = null;
  despesaCapital: DespesaCapitalDto | null = null;
  dadoDespesaRealizada: DadoDespesaDto | null = null;
  caqEscolas: CaqEscolasRespostaDto | null = null;
  memoriaDeCalculo: MemoriaDeCalculoDto | undefined = undefined;

  // Dados processados para a View
  linhasTabelaReceitasRealizadas: LinhaRelatorio[] = [];
  linhasTabelaDespesasCorrentes: LinhaRelatorio[] = [];
  linhasTabelaDespesasCapital: LinhaRelatorio[] = [];

  // Totais e Indicadores
  totalReceitaAnual: number = 0;
  totalDespesaRealizada: number = 0;
  receitaPorAlunoMensal: number = 0;
  caqPorAlunoMensal: number = 0;
  totalMatriculas: number = 0;
  complementacaoNecessaria: number = 0;
  totalDespesaCapital: number = 0;

  larguraQuantidadeReceitaLiquida: number = 0;
  larguraQuantidadeDespesasNecessarias: number = 0;

  dataAtualizacao: string | null = null;

  chartOptions: any;
  private pendingRequests = 0;

  constructor(
    private carregamentoService: CarregamentoService,
    private receitasService: ReceitaEducacaoService,
    private memoriaDeCalculoService: MemoriaDeCalculoService,
    private dadoDespesaService: DadoDespesaService,
    private despesasService: DespesasCorrentesService,
    private escolasCaqService: EscolasCaqService,
    private despesaCapitalService: DespesaCapitalService,
    private layoutResultadoService: LayoutResultadoService,
    private localizacaoService: LocalizacaoService,
    private featureFlagService: FeatureFlagService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.carregamentoService.startLoading();
    this.localizacaoSelecionada =
      this.localizacaoService.obterLocalizacaoSelecionada();
    this.planoSelecionado = this.localizacaoService
      .obterOpcoesPlanoEducacional(this.localizacaoSelecionada!)
      .find(
        (opcao) =>
          opcao.value === this.localizacaoSelecionada?.tipoSimulacaoSelecionado,
      );
    const [escolasCaq, memoriaDeCalculo] = await Promise.all([
      this.escolasCaqService.buscarEscolas(),
      this.memoriaDeCalculoService.buscarMemoriaDeCalculoLocalizacao(),
    ]);

    this.caqEscolas = escolasCaq;
    this.memoriaDeCalculo = memoriaDeCalculo;
    this.totalMatriculas =
      memoriaDeCalculo.resultadoEtapa1NumerosDeMatriculas?.total ?? 0;
    this.caqPorAlunoMensal =
      this.totalMatriculas == 0
        ? 0
        : (memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa?.caqMes || 0) / this.getTotalEscolas();
    try {
      setTimeout(() => {
        this.definaConfiguracoesDaPagina();
      });
      this.buscarDados();
    } catch (error) {
      this.handleRequestError(error, 'definaConfiguracoesDaPagina');
      this.carregamentoService.finishLoading();
    }
  }

  private buscarDados() {
    const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();
    const tipoSimulacao = localizacao?.tipoSimulacaoSelecionado;

    // Caso 1: Nível Nacional (Brasil)
    if (!localizacao?.estadoSelecionado) {
      this.buscarDadosNacional();
      return;
    }

    // Caso 2: Nível Estadual (Plano ou Rede)
    if (localizacao.estadoSelecionado && !localizacao.cidadeSelecionada) {
      const codEstado = Number(localizacao.estadoSelecionado.value);
      if (tipoSimulacao === TipoSimulacao.planejamento) {
        this.buscarDadosPlanoEstadual(codEstado);
      } else {
        this.buscarDadosRedeEstadual(codEstado);
      }
      return;
    }

    // Caso 3: Nível Municipal
    if (localizacao.cidadeSelecionada) {
      const codMunicipio = Number(localizacao.cidadeSelecionada.value);
      this.buscarDadosMunicipal(codMunicipio, tipoSimulacao);
      return;
    }
  }

  private buscarDadosNacional() {
    // 1. Receitas
    this.startRequest();
    this.receitasService
      .buscarPorAno(this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.receitaEspecifica = data;
          this.processarDadosParaTabelaReceitasRealizadas(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'buscarPorAno receitas'),
      });

    // 2. Despesa Líquida Realizada
    this.startRequest();
    this.dadoDespesaService
      .buscarPorAno(this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data: DadoDespesaDto) => {
          this.dadoDespesaRealizada = data;
          this.totalDespesaRealizada = data?.despesaLiquida || 0;
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'buscarPorAno despesa liquida'),
      });

    // 3. Projeção de Despesas Correntes
    this.startRequest();
    this.despesasService
      .porAno(this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesasCorrentes = data;
          this.processarDadosParaTabelaDespesasCorrentes(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porAno despesas correntes'),
      });

    // 4. Despesas de Capital
    this.startRequest();
    this.despesaCapitalService
      .porAno()
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesaCapital = data;
          this.processarDadosParaTabelaDespesasCapital(data);
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porAno despesas capital'),
      });
  }

  private buscarDadosPlanoEstadual(codEstado: number) {
    // 1. Receitas
    this.startRequest();
    this.receitasService
      .buscarPorPlanoEstadual(codEstado, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.receitaEspecifica = data;
          this.processarDadosParaTabelaReceitasRealizadas(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'buscarPorPlanoEstadual receitas'),
      });

    // 2. Despesa Líquida Realizada
    this.startRequest();
    this.dadoDespesaService
      .buscarPorPlanoEstadual(codEstado, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data: DadoDespesaDto) => {
          this.dadoDespesaRealizada = data;
          this.totalDespesaRealizada = data?.despesaLiquida || 0;
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(
            error,
            'buscarPorPlanoEstadual despesa liquida',
          ),
      });

    // 3. Projeção de Despesas Correntes
    this.startRequest();
    this.despesasService
      .porPlanoEstadual(codEstado, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesasCorrentes = data;
          this.processarDadosParaTabelaDespesasCorrentes(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porPlanoEstadual despesas correntes'),
      });

    // 4. Despesas de Capital
    this.startRequest();
    this.despesaCapitalService
      .porPlanoEstadual(codEstado)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesaCapital = data;
          this.processarDadosParaTabelaDespesasCapital(data);
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porPlanoEstadual despesas capital'),
      });
  }

  private buscarDadosRedeEstadual(codEstado: number) {
    // 1. Receitas
    this.startRequest();
    this.receitasService
      .buscarPorRedeEstadual(codEstado, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.receitaEspecifica = data;
          this.processarDadosParaTabelaReceitasRealizadas(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'buscarPorRedeEstadual receitas'),
      });

    // 2. Despesa Líquida Realizada
    this.startRequest();
    this.dadoDespesaService
      .buscarPorRedeEstadual(codEstado, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data: DadoDespesaDto) => {
          this.dadoDespesaRealizada = data;
          this.totalDespesaRealizada = data?.despesaLiquida || 0;
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(
            error,
            'buscarPorRedeEstadual despesa liquida',
          ),
      });

    // 3. Projeção de Despesas Correntes
    this.startRequest();
    this.despesasService
      .porRedeEstadual(codEstado, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesasCorrentes = data;
          this.processarDadosParaTabelaDespesasCorrentes(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porRedeEstadual despesas correntes'),
      });

    // 4. Despesas de Capital
    this.startRequest();
    this.despesaCapitalService
      .porRedeEstadual(codEstado)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesaCapital = data;
          this.processarDadosParaTabelaDespesasCapital(data);
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porRedeEstadual despesas capital'),
      });
  }

  private buscarDadosMunicipal(
    codigoMunicipio: number,
    tipoSimulacao?: TipoSimulacao,
  ) {
    // 1. Receitas
    this.startRequest();
    this.receitasService
      .buscarPorMunicipio(codigoMunicipio, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.receitaEspecifica = data;
          this.processarDadosParaTabelaReceitasRealizadas(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'buscarPorMunicipio receitas'),
      });

    // 2. Despesa Líquida Realizada (RPEb do banco)
    this.startRequest();
    this.dadoDespesaService
      .buscarPorMunicipio(codigoMunicipio, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data: DadoDespesaDto) => {
          this.dadoDespesaRealizada = data;
          this.totalDespesaRealizada = data?.despesaLiquida || 0;
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'buscarPorMunicipio despesa liquida'),
      });

    // 3. Projeção de Despesas Correntes
    this.startRequest();
    this.despesasService
      .porMunicipio(codigoMunicipio, this.anoProjecao)
      .pipe(finalize(() => this.endRequest()))
      .subscribe({
        next: (data) => {
          this.despesasCorrentes = data;
          this.processarDadosParaTabelaDespesasCorrentes(data);
          this.calcularComplementacao();
          this.configurarGraficoComparativo();
          this.definaLarguraGraficoComplementacaoNecessaria();
          this.cdr.markForCheck();
        },
        error: (error) =>
          this.handleRequestError(error, 'porMunicipio despesas correntes'),
      });

    // 4. Despesas de Capital
    this.startRequest();
    const requestCapital =
      tipoSimulacao === TipoSimulacao.planejamento
        ? this.despesaCapitalService.calcularPlanoPorMunicipio(codigoMunicipio)
        : this.despesaCapitalService.calcularRedePorMunicipio(codigoMunicipio);

    requestCapital.pipe(finalize(() => this.endRequest())).subscribe({
      next: (data) => {
        this.despesaCapital = data;
        this.processarDadosParaTabelaDespesasCapital(data);
        this.cdr.markForCheck();
      },
      error: (error) =>
        this.handleRequestError(error, 'porMunicipio despesa capital'),
    });
  }

  getTotalEscolas(): number {
    return this.caqEscolas?.totalInstituicoes || 0;
  }

  private startRequest() {
    this.pendingRequests += 1;
  }

  private endRequest() {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) {
      this.carregamentoService.finishLoading();
    }
  }

  private handleRequestError(error: unknown, context: string) {
    console.error(`[relatorio-receitas-despesas] ${context}`, error);
  }

  getTotalMatriculas() {
    return (
      this.memoriaDeCalculo?.resultadoEtapa1NumerosDeMatriculas?.total || 0
    );
  }

  atualizarReceitaPorAlunoMensal() {
    const totalMatricula = this.getTotalMatriculas();
    if (this.totalReceitaAnual && totalMatricula != 0) {
      return (this.receitaPorAlunoMensal =
        this.totalReceitaAnual / totalMatricula / 12);
    }

    return 0;
  }

  private configurarGraficoComparativo() {
    if (!this.totalReceitaAnual || !this.despesasCorrentes) return;

    const receitaRPEB = this.totalReceitaAnual;
    const despesaNecessaria = this.despesasCorrentes.total;

    this.chartOptions = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        formatter: (params: any) => {
          return params
            .map(
              (p: any) =>
                `${p.marker} ${p.seriesName}: <b>R$ ${p.value.toLocaleString('pt-BR')}</b>`,
            )
            .join('<br/>');
        },
      },
      legend: {
        data: ['receita líquida (RPEB)', 'despesa corrente necessária'],
        bottom: 0,
        icon: 'circle',
        textStyle: { color: '#666', fontSize: 13 },
        itemGap: 30,
      },
      grid: {
        left: '2%',
        right: '25%',
        top: '30%',
        bottom: '20%',
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        show: false,
        max: Math.max(receitaRPEB, despesaNecessaria) * 1.1,
      },
      yAxis: {
        type: 'category',
        data: [''],
        show: false,
      },
      series: [
        {
          name: 'receita líquida (RPEB)',
          type: 'bar',
          data: [receitaRPEB],
          barWidth: 32,
          itemStyle: {
            color: '#f3b062',
            borderRadius: [4, 4, 4, 4],
          },
          label: {
            show: true,
            position: 'top',
            distance: 10,
            formatter: () => `R$ ${receitaRPEB.toLocaleString('pt-BR')}`,
            color: '#f3b062',
            fontWeight: 'bold',
            fontSize: 13,
          },
          z: 3,
        },
        {
          name: 'despesa corrente necessária',
          type: 'bar',
          data: [despesaNecessaria],
          barGap: '-100%',
          barWidth: 50,
          itemStyle: {
            color: '#ff7c7c',
            borderRadius: [4, 4, 4, 4],
            opacity: 0.6,
          },
          label: {
            show: true,
            position: 'right',
            distance: 15,
            formatter: () => `R$ ${despesaNecessaria.toLocaleString('pt-BR')}`,
            color: '#ff7c7c',
            fontWeight: 'bold',
            fontSize: 13,
          },
          z: 2,
        },
      ],
    };
  }

  private calcularComplementacao() {
    if (this.despesasCorrentes && this.totalDespesaRealizada) {
      this.complementacaoNecessaria = Math.max(
        0,
        this.despesasCorrentes.total - this.totalDespesaRealizada,
      );
    }
  }

  private processarDadosParaTabelaReceitasRealizadas(data: ReceitaEducacaoDto) {
    if (!data) return;
    this.totalReceitaAnual = data.receitaRealizadaTotal ?? 0;
    const calcPct = (val: number) =>
      this.totalReceitaAnual === 0 ? 0 : (val / this.totalReceitaAnual) * 100;

    const totalFundeb =
      (data.fundoEstadual ?? 0) +
      (data.complementacaoVaaf ?? 0) +
      (data.complementacaoVaat ?? 0) +
      (data.complementacaoVaar ?? 0);
    const totalFNDE =
      (data.pnae ?? 0) +
      (data.pnate ?? 0) +
      (data.pdde ?? 0) +
      (data.pnld ?? 0);

    this.linhasTabelaReceitasRealizadas = [
      {
        id: 1,
        descricao: '1. Receita Líquida de Impostos e Transferências (RLI)',
        valor: data.receitaImpostosTransferencias ?? 0,
        percentual: calcPct(data.receitaImpostosTransferencias ?? 0),
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 2,
        descricao: '2. Fundeb (total)',
        valor: totalFundeb,
        percentual: calcPct(totalFundeb),
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 21,
        descricao: '2.1. Fundo estadual',
        valor: data.fundoEstadual ?? 0,
        percentual: calcPct(data.fundoEstadual ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 22,
        descricao: '2.2. Complementação da União VAAF',
        valor: data.complementacaoVaaf ?? 0,
        percentual: calcPct(data.complementacaoVaaf ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 23,
        descricao: '2.3. Complementação da União VAAT',
        valor: data.complementacaoVaat ?? 0,
        percentual: calcPct(data.complementacaoVaat ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 24,
        descricao: '2.4. Complementação da União VAAR',
        valor: data.complementacaoVaar ?? 0,
        percentual: calcPct(data.complementacaoVaar ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 3,
        descricao: '3. Salário Educação (cotas estadual e municipal)',
        valor: data.salarioEducacao ?? 0,
        percentual: calcPct(data.salarioEducacao ?? 0),
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 4,
        descricao: '4. Programas de transferência universal (FNDE)',
        valor: totalFNDE,
        percentual: calcPct(totalFNDE),
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 41,
        descricao: '4.1. PNAE',
        valor: data.pnae ?? 0,
        percentual: calcPct(data.pnae ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 42,
        descricao: '4.2. PNATE',
        valor: data.pnate ?? 0,
        percentual: calcPct(data.pnate ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 43,
        descricao: '4.3. PDDE',
        valor: data.pdde ?? 0,
        percentual: calcPct(data.pdde ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 44,
        descricao: '4.4. PNLD',
        valor: data.pnld ?? 0,
        percentual: calcPct(data.pnld ?? 0),
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 5,
        descricao: '5. Royalties de Petróleo e Gás Natural',
        valor: data.royaltiesPetroleoGas ?? 0,
        percentual: calcPct(data.royaltiesPetroleoGas ?? 0),
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 99,
        descricao: 'Total',
        valor: this.totalReceitaAnual,
        percentual: 100,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
    ];
  }

  private processarDadosParaTabelaDespesasCorrentes(
    data: DespesasCorrentesDto,
  ) {
    if (!data) return;
    this.linhasTabelaDespesasCorrentes = [
      {
        id: 1,
        descricao: '1. Pessoal',
        valor: data.pessoal?.valor ?? 0,
        percentual: data.pessoal?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 11,
        descricao: '1.1. Docentes',
        valor: data.subPessoal?.docentes?.valor ?? 0,
        percentual: data.subPessoal?.docentes?.percentual ?? 0,
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 12,
        descricao: '1.2. Funcionários da unidade de ensino (não docentes)',
        valor: data.subPessoal?.naoDocentes?.valor ?? 0,
        percentual: data.subPessoal?.naoDocentes?.percentual ?? 0,
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 13,
        descricao: '1.3. Assistentes de EI de sala de aula',
        valor: data.subPessoal?.auxiliares?.valor ?? 0,
        percentual: data.subPessoal?.auxiliares?.percentual ?? 0,
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 14,
        descricao: '1.4. Encargos Sociais',
        valor: data.subPessoal?.encargos?.valor ?? 0,
        percentual: data.subPessoal?.encargos?.percentual ?? 0,
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 2,
        descricao: '2. Área administrativa da rede',
        valor: data.areaAdministrativa?.valor ?? 0,
        percentual: data.areaAdministrativa?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 3,
        descricao: '3. Autonomia Financeira das Unidades de Ensino',
        valor: data.autonomiaFinanceira?.valor ?? 0,
        percentual: data.autonomiaFinanceira?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 4,
        descricao: '4. Manutenção de bens e serviços',
        valor: data.manutencaoBensServicos?.valor ?? 0,
        percentual: data.manutencaoBensServicos?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 5,
        descricao: '5. Formação e valorização dos profissionais da Educação',
        valor: data.formacaoValorizacao?.valor ?? 0,
        percentual: data.formacaoValorizacao?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 6,
        descricao: '6. Serviços diversos',
        valor: data.servicosDiversos?.valor ?? 0,
        percentual: data.servicosDiversos?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 7,
        descricao: '7. Alimentação escolar',
        valor: data.alimentacaoEscolar?.valor ?? 0,
        percentual: data.alimentacaoEscolar?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 99,
        descricao: 'Total',
        valor: data.total ?? 0,
        percentual: 100,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
    ];
  }

  private processarDadosParaTabelaDespesasCapital(data: DespesaCapitalDto) {
    if (!data) return;

    this.totalDespesaCapital = data.totalGeral;

    this.linhasTabelaDespesasCapital = [
      {
        id: 1,
        descricao:
          '1. Ampliação da rede física (construção de novas escolas e salas de aula)',
        valor: data.ampliacaoRedeFisica?.valor ?? 0,
        percentual: data.ampliacaoRedeFisica?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 11,
        descricao: '1.1. Educação infantil',
        valor: data.ampliacaoRedeFisicaEducacaoInfantil?.valor ?? 0,
        percentual: data.ampliacaoRedeFisicaEducacaoInfantil?.percentual ?? 0,
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 12,
        descricao:
          '1.2. Ensino fundamental, Ensino médio e Educação de Jovens e Adultos',
        valor: data.ampliacaoRedeFisicaEnsinoFundamental?.valor ?? 0,
        percentual: data.ampliacaoRedeFisicaEnsinoFundamental?.percentual ?? 0,
        isTitulo: false,
        nivelRecuo: 1,
        mostrarBarra: true,
      },
      {
        id: 2,
        descricao: '2. Ampliação de dependências nas unidades existentes',
        valor: data.ampliacaoDependencias?.valor ?? 0,
        percentual: data.ampliacaoDependencias?.percentual ?? 0,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
      {
        id: 99,
        descricao: 'Total',
        valor: data.totalGeral ?? 0,
        percentual: 100,
        isTitulo: true,
        nivelRecuo: 0,
        mostrarBarra: true,
      },
    ];
  }

  definaLarguraGraficoComplementacaoNecessaria() {
    const proporcaoMaximaEmPx = 600;
    const despesaCorrente = this.despesasCorrentes?.total
      ? this.despesasCorrentes?.total
      : 0;
    const total = despesaCorrente + this.totalReceitaAnual;
    this.larguraQuantidadeReceitaLiquida =
      total > 0
        ? +((this.totalReceitaAnual / total) * proporcaoMaximaEmPx).toFixed(2)
        : 0;
    this.larguraQuantidadeDespesasNecessarias =
      total > 0
        ? +((despesaCorrente / total) * proporcaoMaximaEmPx).toFixed(2)
        : 0;
  }

  async definaConfiguracoesDaPagina() {
    this.layoutResultadoService.setTitulo(
      'Receitas e despesas da educação básica',
    );
    this.layoutResultadoService.setCorTexto('text-yellow-700');
    this.layoutResultadoService.setCorBordaTitulo('border-yellow-600');
    this.layoutResultadoService.setCorTextoRodape('text-yellow-700');
    this.layoutResultadoService.setCorBotaoRodape('bg-yellow-700');
    this.layoutResultadoService.setCorBordaBotaoRodape('border-yellow-700');
    this.layoutResultadoService.setAno(String(this.anoProjecao));
    this.layoutResultadoService.setTextoComoCitar(this.textoParaCopiar);
    this.configurarInformacoesLocalizacao();
    this.definirDownloads();
  }

  private configurarInformacoesLocalizacao(): void {
    const localizacaoSelecionada =
      this.localizacaoService.obterLocalizacaoSelecionada();

    if (localizacaoSelecionada) {
      if (this.planoSelecionado?.label) {
        this.layoutResultadoService.setRedeSelecionada(
          this.planoSelecionado.label,
        );
      }

      this.layoutResultadoService.setPais('Brasil');

      if (localizacaoSelecionada.estadoSelecionado) {
        this.dependenciaAdministrativa = 'Estadual';
        this.codigoIbge = localizacaoSelecionada.estadoSelecionado.value;
        this.uf = localizacaoSelecionada.estadoSelecionado.label;
        this.layoutResultadoService.setEstado(
          localizacaoSelecionada.estadoSelecionado.sigla,
        );
      }

      if (localizacaoSelecionada.cidadeSelecionada) {
        this.dependenciaAdministrativa = 'Municipal';
        this.municipio = localizacaoSelecionada.cidadeSelecionada.label;
        this.codigoIbge = localizacaoSelecionada.cidadeSelecionada.value;
        this.layoutResultadoService.setCidade(
          localizacaoSelecionada.cidadeSelecionada.label,
        );
      }

      // Configurar rota para voltar à seleção de local
      this.layoutResultadoService.setRotaFiltroLocalidade(
        '/selecionar-local-R5',
      );
    } else {
      // Definir valores padrão
      this.layoutResultadoService.setPais('Brasil');
      this.layoutResultadoService.setEstado('Nível Nacional');
      this.layoutResultadoService.setCidade('');

      this.layoutResultadoService.setRotaFiltroLocalidade(
        '/selecionar-local-R5',
      );
    }
  }

  private definirDownloads(): void {
    const rotas: DownloadRota[] = [];

    if (this.featureFlagService.funcionalidadeHabilitada('883-r5-download-pdf')) {
      rotas.push({
        acao: () => this.baixarPdfCompleto(),
        texto: 'Baixe esta página em PDF',
        tipo: 'acao',
      });
    }

    if (this.featureFlagService.funcionalidadeHabilitada('882-r5-download-csv')) {
      rotas.push({
        acao: () => this.exportarReceitasDespesasCSV(),
        texto: 'Baixe os valores do CAQ em CSV',
        tipo: 'acao',
      });
    }

    if(rotas.length > 0){
      this.layoutResultadoService.setDownloads(rotas);
    }
  }

  // Métodos para o PDF completo
  async baixarPdfCompleto(): Promise<void> {
    this.carregamentoService.startLoading();
    const container = document.querySelector('.container') as HTMLElement;
    if (!container) {
      console.error('Container não encontrado para geração do PDF');
      return;
    }

    try {
      const localizacao = this.obterTextoDownloadPorLocalidade();
      const headerInfo = this.criarHeaderInfo();
      await PdfUtils.baixarElementoComoPdf(
        container,
        'Relatorio_Receitas_Despesas',
        'Receitas Despesas',
        localizacao,
        '2023',
        headerInfo,
      );
      this.carregamentoService.finishLoading();
    } catch (error) {
      console.error('Erro ao gerar PDF completo:', error);
      this.carregamentoService.finishLoading();
    }
  }

  private obterTextoDownloadPorLocalidade(): string {
    const localizacao = this.localizacaoSelecionada;

    if (localizacao?.cidadeSelecionada) {
      return 'Baixar dados do Município';
    }

    if (localizacao?.estadoSelecionado) {
      return 'Baixar dados por localidade da UF';
    }

    return 'Baixar dados por localidade do Brasil';
  }

  private criarHeaderInfo(): HeaderInfo {
    const localizacao = this.localizacaoSelecionada;

    return {
      titulo: 'Receitas e despesas da Educação Básica',
      tituloModo: 'Resultados',
      subtitulo: 'Resultados das projeções do SimCAQ para a localidade',
      ilustracao: this.layoutResultadoService.getImagemFundoAtual(),
      breadcrumb: {
        plano: this.planoSelecionado?.label || 'Plano não selecionado',
        pais: 'Brasil',
        estado: localizacao?.estadoSelecionado?.sigla || 'BR',
        cidade: localizacao?.cidadeSelecionada?.label,
        ano: '2023',
      },
    };
  }

  exportarReceitasDespesasCSV(): void {
    if (
      !this.receitaEspecifica ||
      !this.despesasCorrentes ||
      !this.despesaCapital ||
      !this.dadoDespesaRealizada
    ) {
      console.error('Dados não carregados completamente');
      return;
    }

    const localizacao = this.localizacaoSelecionada;
    let locationData = `Ano;${this.anoRealizado}\n`;

    if (localizacao?.estadoSelecionado) {
      locationData += `Código UF;${localizacao.estadoSelecionado.value}\n`;
      locationData += `UF;${localizacao.estadoSelecionado.label}\n`;
    }

    if (localizacao?.cidadeSelecionada) {
      locationData += `Código Município;${localizacao.cidadeSelecionada.value}\n`;
      locationData += `Município;${localizacao.cidadeSelecionada.label}\n`;
    }

    locationData += '\n';

    // Funções auxiliares para gerar o CSV
    const arredondar = (valor: number): number => Math.round(valor);
    const formatarPercentual = (valor: number): string =>
      valor.toFixed(1).replace('.', ',');
    const calcPerc = (parte: number, total: number): string =>
      total > 0 ? formatarPercentual((parte / total) * 100) : '0,0';

    // Valores calculados - Receitas
    const fundebTotal =
      (this.receitaEspecifica.fundoEstadual ?? 0) +
      (this.receitaEspecifica.complementacaoVaaf ?? 0) +
      (this.receitaEspecifica.complementacaoVaat ?? 0) +
      (this.receitaEspecifica.complementacaoVaar ?? 0);

    const programasTransferencia =
      (this.receitaEspecifica.pnae ?? 0) +
      (this.receitaEspecifica.pnate ?? 0) +
      (this.receitaEspecifica.pdde ?? 0) +
      (this.receitaEspecifica.pnld ?? 0);

    const receitaTotal = this.receitaEspecifica.receitaRealizadaTotal ?? 0;
    const despesaTotal = this.despesasCorrentes.total ?? 0;
    const pessoalTotal = this.despesasCorrentes.pessoal?.valor ?? 0;
    const capitalTotal = this.despesaCapital.totalGeral ?? 0;

    // Complementação CAQ
    const complementacaoNecessaria =
      despesaTotal > receitaTotal ? despesaTotal - receitaTotal : 0;
    const percComplementacao =
      despesaTotal > receitaTotal ? (despesaTotal / receitaTotal - 1) * 100 : 0;

    // Construir colunas do CSV
    let csv = locationData;

    const appendData = (
      category: string,
      item: string,
      value: number,
      perc?: string,
    ) => {
      const percStr = perc !== undefined ? perc : '';
      csv += `${category};${item};${arredondar(value)};${percStr}\n`;
    };

    csv += `Categoria;Descrição do Item;Valor Estimado (R$);Participação Relativa (%)\n`;

    appendData(
      'Receitas Realizadas',
      'Receita Líquida de Impostos e Transferências (RLI)',
      this.receitaEspecifica.receitaImpostosTransferencias ?? 0,
      calcPerc(
        this.receitaEspecifica.receitaImpostosTransferencias ?? 0,
        receitaTotal,
      ),
    );
    appendData(
      'Receitas Realizadas',
      'Fundo Estadual',
      this.receitaEspecifica.fundoEstadual ?? 0,
      calcPerc(this.receitaEspecifica.fundoEstadual ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Complementação da União VAAF',
      this.receitaEspecifica.complementacaoVaaf ?? 0,
      calcPerc(this.receitaEspecifica.complementacaoVaaf ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Complementação da União VAAT',
      this.receitaEspecifica.complementacaoVaat ?? 0,
      calcPerc(this.receitaEspecifica.complementacaoVaat ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Complementação da União VAAR',
      this.receitaEspecifica.complementacaoVaar ?? 0,
      calcPerc(this.receitaEspecifica.complementacaoVaar ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Total Fundeb',
      fundebTotal,
      calcPerc(fundebTotal, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Salário Educação',
      this.receitaEspecifica.salarioEducacao ?? 0,
      calcPerc(this.receitaEspecifica.salarioEducacao ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'PNAE',
      this.receitaEspecifica.pnae ?? 0,
      calcPerc(this.receitaEspecifica.pnae ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'PNATE',
      this.receitaEspecifica.pnate ?? 0,
      calcPerc(this.receitaEspecifica.pnate ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'PDDE',
      this.receitaEspecifica.pdde ?? 0,
      calcPerc(this.receitaEspecifica.pdde ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'PNLD',
      this.receitaEspecifica.pnld ?? 0,
      calcPerc(this.receitaEspecifica.pnld ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Total Programas de Transferência Universal',
      programasTransferencia,
      calcPerc(programasTransferencia, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Royalties de Petróleo e Gás Natural',
      this.receitaEspecifica.royaltiesPetroleoGas ?? 0,
      calcPerc(this.receitaEspecifica.royaltiesPetroleoGas ?? 0, receitaTotal),
    );
    appendData(
      'Receitas Realizadas',
      'Total de Receitas Realizadas',
      receitaTotal,
      '100,0',
    );
    csv += '\n';

    appendData(
      'Despesas Correntes Necessárias',
      'Despesas com Docentes',
      this.despesasCorrentes.subPessoal?.docentes?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.subPessoal?.docentes?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Despesas com Funcionários não Docentes',
      this.despesasCorrentes.subPessoal?.naoDocentes?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.subPessoal?.naoDocentes?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Despesas com Auxiliares',
      this.despesasCorrentes.subPessoal?.auxiliares?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.subPessoal?.auxiliares?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Encargos e Obrigações Trabalhistas',
      this.despesasCorrentes.subPessoal?.encargos?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.subPessoal?.encargos?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Total Despesas com Pessoal',
      pessoalTotal,
      formatarPercentual(this.despesasCorrentes.pessoal?.percentual ?? 0),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Custeio da Área Administrativa',
      this.despesasCorrentes.areaAdministrativa?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.areaAdministrativa?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Autonomia Financeira das Unidades de Ensino',
      this.despesasCorrentes.autonomiaFinanceira?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.autonomiaFinanceira?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Manutenção de Bens e Serviços',
      this.despesasCorrentes.manutencaoBensServicos?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.manutencaoBensServicos?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Formação e Valorização Profissional',
      this.despesasCorrentes.formacaoValorizacao?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.formacaoValorizacao?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Serviços Diversos',
      this.despesasCorrentes.servicosDiversos?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.servicosDiversos?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Alimentação Escolar',
      this.despesasCorrentes.alimentacaoEscolar?.valor ?? 0,
      formatarPercentual(
        this.despesasCorrentes.alimentacaoEscolar?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas Correntes Necessárias',
      'Total de Despesas Correntes Necessárias',
      despesaTotal,
      '100,0',
    );
    csv += '\n';

    appendData(
      'Despesas de Capital Necessárias',
      'Ampliação de Rede Física - Novas Salas/Escolas',
      this.despesaCapital.ampliacaoRedeFisica?.valor ?? 0,
      formatarPercentual(
        this.despesaCapital.ampliacaoRedeFisica?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas de Capital Necessárias',
      'Ampliação de Dependências em Unidades Existentes',
      this.despesaCapital.ampliacaoDependencias?.valor ?? 0,
      formatarPercentual(
        this.despesaCapital.ampliacaoDependencias?.percentual ?? 0,
      ),
    );
    appendData(
      'Despesas de Capital Necessárias',
      'Total de Despesas de Capital Necessárias',
      capitalTotal,
      '100,0',
    );
    csv += '\n';

    appendData(
      'Indicadores Finais',
      'Despesa Total Liquidada Realizada',
      this.dadoDespesaRealizada.despesaLiquida ?? 0,
    );
    appendData(
      'Indicadores Finais',
      'Complementação Necessária (CAQ)',
      complementacaoNecessaria,
      formatarPercentual(percComplementacao),
    );

    // Configurações de Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const dataAtual = new Date().toISOString().split('T')[0];

    link.href = URL.createObjectURL(blob);
    link.download = `relatorio_receita_despesa_${dataAtual}.csv`;
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
